/**
 * Recommender API Integration
 * Handles Shopify Customer API and Discount API via backend proxy
 */

window.RecommenderAPI = (function() {
  'use strict';

  // ========== CONSTANTS ==========

  // API endpoint - Cloudflare Worker
  // REQUIRED: Set window.RECOMMENDER_API_URL in theme.liquid before this script loads
  // Example: window.RECOMMENDER_API_URL = 'https://recommender-api-production.upahar.workers.dev';
  const API_BASE = window.RECOMMENDER_API_URL || null;

  // Log warning if API URL not configured (backend sync will be disabled)
  if (!API_BASE) {
    console.warn('[RecommenderAPI] window.RECOMMENDER_API_URL not set. Backend sync disabled.');
  }

  // Static discount code - always works even without API
  const DISCOUNT_CODE = 'FIRST10';

  // Time constants (in milliseconds)
  const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
  const MAX_RETRY_QUEUE_SIZE = 10;

  // Lock to prevent concurrent retry processing
  let isProcessingRetryQueue = false;

  // Analytics event names (prevents typos)
  const EVENTS = {
    EMAIL_CAPTURED: 'email_captured',
    QUIZ_STARTED: 'quiz_started',
    QUIZ_COMPLETED: 'quiz_completed',
    PRODUCT_ADDED: 'product_added_from_quiz'
  };

  // Track if API is available
  let apiAvailable = null;

  /**
   * Create or update a Shopify customer with quiz data
   * @param {Object} data - Customer and quiz data
   * @returns {Promise<Object>} Customer response
   */
  async function createOrUpdateCustomer(data) {
    const {
      email,
      petName,
      breed,
      phone,
      quizType,
      selections,
      recommendations,
      marketingConsent
    } = data;

    // Build customer tags
    const tags = buildCustomerTags(quizType, selections, breed);

    // Build metafields for quiz data
    const metafields = buildQuizMetafields(quizType, petName, breed, selections, recommendations);

    const payload = {
      customer: {
        email: email,
        phone: phone || null,
        tags: tags.join(', '),
        accepts_marketing: marketingConsent !== false,
        metafields: metafields
      }
    };

    // Skip if API URL not configured
    if (!API_BASE) {
      console.log('[RecommenderAPI] Customer sync skipped (API_BASE not configured)');
      return null;
    }

    try {
      const response = await fetch(`${API_BASE}/customers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Customer API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('[RecommenderAPI] Customer creation failed:', error);
      // Return null but don't break the flow
      return null;
    }
  }

  /**
   * Build customer tags from quiz data
   */
  function buildCustomerTags(quizType, selections, breed) {
    const tags = [`quiz:${quizType}`];

    if (selections) {
      if (selections.concern) tags.push(`concern:${selections.concern}`);
      if (selections.petType) tags.push(`pet:${selections.petType}`);
      if (selections.age) tags.push(`age:${selections.age}`);
      if (selections.symptoms && selections.symptoms.length) {
        tags.push(`symptoms:${selections.symptoms.length}`);
      }
    }

    if (breed) tags.push(`breed:${breed}`);

    return tags;
  }

  /**
   * Build metafields for storing customer and quiz data
   * pet_name and breed go in 'pet' namespace for personalization
   * quiz data goes in 'quiz_data' namespace
   */
  function buildQuizMetafields(quizType, petName, breed, selections, recommendations) {
    const metafields = [];

    // Pet info - separate namespace for easy personalization access
    if (petName) {
      metafields.push({
        namespace: 'pet',
        key: 'name',
        value: petName,
        type: 'single_line_text_field'
      });
    }

    if (breed) {
      metafields.push({
        namespace: 'pet',
        key: 'breed',
        value: breed,
        type: 'single_line_text_field'
      });
    }

    // Quiz data - for analytics and follow-up
    metafields.push({
      namespace: 'quiz_data',
      key: 'quiz_type',
      value: quizType,
      type: 'single_line_text_field'
    });

    metafields.push({
      namespace: 'quiz_data',
      key: 'quiz_date',
      value: new Date().toISOString(),
      type: 'date_time'
    });

    if (selections) {
      metafields.push({
        namespace: 'quiz_data',
        key: 'selections',
        value: JSON.stringify(selections),
        type: 'json'
      });
    }

    if (recommendations && recommendations.length) {
      const handles = recommendations.map(r => r.id || r.handle || r);
      metafields.push({
        namespace: 'quiz_data',
        key: 'recommendations',
        value: JSON.stringify(handles),
        type: 'json'
      });
    }

    return metafields;
  }

  /**
   * Full email capture flow
   * Works independently - API calls happen in background (fire and forget)
   * @param {Object} formData - Form data from quiz
   * @returns {Object} Result with discount code (immediate, no await needed)
   */
  function captureEmail(formData) {
    // Validate CSRF nonce if available
    if (window.RecommenderFormUtils && window.RecommenderFormUtils.validateCsrfNonce) {
      if (!window.RecommenderFormUtils.validateCsrfNonce()) {
        console.error('[RecommenderAPI] CSRF validation failed');
        return {
          success: false,
          error: 'Security validation failed. Please refresh the page and try again.'
        };
      }
    }

    const {
      email,
      petName,
      breed,
      phone,
      quizType,
      selections,
      recommendations,
      marketingConsent
    } = formData;

    // Save to localStorage immediately (works offline)
    saveLocalQuizData(quizType, {
      email,
      petName,
      breed,
      selections,
      recommendations: recommendations ? recommendations.map(r => r.id || r) : [],
      discountCode: DISCOUNT_CODE,
      capturedAt: Date.now()
    });

    // Track the capture
    trackEvent(EVENTS.EMAIL_CAPTURED, {
      quiz_type: quizType,
      has_phone: !!phone,
      has_pet_name: !!petName,
      has_breed: !!breed
    });

    // Fire and forget - sync to backend APIs (Shopify + Brevo)
    // This runs in background, doesn't block the user
    syncToBackend({
      email,
      petName,
      breed,
      phone,
      quizType,
      selections,
      recommendations,
      marketingConsent
    }).catch(err => console.warn('[RecommenderAPI] Background sync failed:', err));

    // Return immediately with discount code
    return {
      success: true,
      discountCode: DISCOUNT_CODE
    };
  }

  /**
   * Sync data to backend APIs (runs in background)
   * Handles Shopify Customer and Brevo contact creation
   */
  async function syncToBackend(data) {
    // Skip if API URL not configured
    if (!API_BASE) {
      console.log('[RecommenderAPI] Backend sync skipped (API_BASE not configured)');
      return null;
    }

    try {
      const response = await fetch(`${API_BASE}/capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const result = await response.json();
      console.log('[RecommenderAPI] Synced to backend:', result);
      return result;
    } catch (error) {
      console.warn('[RecommenderAPI] Backend sync failed:', error);
      // Store for retry later
      queueForRetry(data);
      return null;
    }
  }

  /**
   * Queue failed submissions for retry
   * Uses atomic read-modify-write to avoid race conditions
   */
  function queueForRetry(data) {
    try {
      const storage = getStorage();
      // Atomic: read current queue, add item, write back
      const queue = JSON.parse(storage.getItem('recommender_sync_queue') || '[]');
      queue.push({ ...data, queuedAt: Date.now() });
      storage.setItem('recommender_sync_queue', JSON.stringify(queue.slice(-MAX_RETRY_QUEUE_SIZE)));
    } catch (e) {
      // Ignore storage errors
    }
  }

  /**
   * Retry queued submissions (call on page load)
   * Uses lock to prevent concurrent processing and atomic queue operations
   */
  async function retryQueuedSubmissions() {
    // Prevent concurrent processing
    if (isProcessingRetryQueue) {
      console.log('[RecommenderAPI] Retry queue already being processed, skipping');
      return;
    }

    try {
      isProcessingRetryQueue = true;
      const storage = getStorage();

      // Atomically get and clear the queue to prevent race conditions
      const queue = JSON.parse(storage.getItem('recommender_sync_queue') || '[]');
      if (queue.length === 0) {
        isProcessingRetryQueue = false;
        return;
      }

      // Clear queue immediately - failed items will be re-added by syncToBackend
      storage.setItem('recommender_sync_queue', '[]');

      // Process each item - failures are automatically re-queued by syncToBackend
      for (const item of queue) {
        try {
          await syncToBackend(item);
        } catch (e) {
          // syncToBackend handles its own queueing on failure
        }
      }
    } catch (e) {
      // Ignore errors
    } finally {
      isProcessingRetryQueue = false;
    }
  }

  /**
   * Get available storage (localStorage with sessionStorage fallback)
   */
  function getStorage() {
    try {
      localStorage.setItem('_test', '1');
      localStorage.removeItem('_test');
      return localStorage;
    } catch (e) {
      console.warn('[RecommenderAPI] localStorage unavailable, using sessionStorage');
      return sessionStorage;
    }
  }

  /**
   * Save quiz data locally (with sessionStorage fallback)
   */
  function saveLocalQuizData(quizType, data) {
    const key = `recommender_${quizType}`;
    const value = JSON.stringify({ ...data, timestamp: Date.now() });

    try {
      getStorage().setItem(key, value);
    } catch (e) {
      // Quota exceeded - try to clear old data and retry
      console.warn('[RecommenderAPI] Storage quota exceeded, clearing old data');
      try {
        clearOldQuizData();
        getStorage().setItem(key, value);
      } catch (e2) {
        console.error('[RecommenderAPI] Failed to save quiz data:', e2);
      }
    }
  }

  /**
   * Clear old quiz data to free up space
   */
  function clearOldQuizData() {
    const storage = getStorage();
    const keysToRemove = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && key.startsWith('recommender_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => storage.removeItem(key));
  }

  /**
   * Get saved quiz data
   */
  function getLocalQuizData(quizType) {
    try {
      const key = `recommender_${quizType}`;
      const data = getStorage().getItem(key);
      if (!data) return null;

      const parsed = JSON.parse(data);
      // Expire after 30 days
      if (Date.now() - parsed.timestamp > THIRTY_DAYS_MS) {
        getStorage().removeItem(key);
        return null;
      }
      return parsed;
    } catch (e) {
      return null;
    }
  }

  // Debounce tracking to prevent rapid duplicate events
  const recentEvents = new Map();
  const DEBOUNCE_MS = 500;

  /**
   * Track analytics event (debounced to prevent rapid duplicates)
   */
  function trackEvent(eventName, data = {}) {
    // Debounce: skip if same event fired within 500ms
    const eventKey = `${eventName}:${JSON.stringify(data)}`;
    const lastTime = recentEvents.get(eventKey);
    if (lastTime && Date.now() - lastTime < DEBOUNCE_MS) {
      return; // Skip duplicate
    }
    recentEvents.set(eventKey, Date.now());

    // Clean up old entries periodically
    if (recentEvents.size > 50) {
      const now = Date.now();
      for (const [key, time] of recentEvents) {
        if (now - time > DEBOUNCE_MS * 2) recentEvents.delete(key);
      }
    }

    // GA4
    if (window.gtag) {
      window.gtag('event', eventName, {
        event_category: 'recommender_api',
        ...data
      });
    }

    // Facebook Pixel
    if (window.fbq) {
      window.fbq('trackCustom', eventName, data);
    }

    // GTM dataLayer
    if (window.dataLayer) {
      window.dataLayer.push({
        event: eventName,
        ...data
      });
    }

    console.log('[RecommenderAPI]', eventName, data);
  }

  /**
   * Check if API backend is available
   */
  async function checkAPIAvailability() {
    // Return false if API URL not configured
    if (!API_BASE) {
      return false;
    }

    try {
      const response = await fetch(`${API_BASE}/health`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      return response.ok;
    } catch (e) {
      return false;
    }
  }

  /**
   * Get the discount code
   */
  function getDiscountCode() {
    return DISCOUNT_CODE;
  }

  // Retry queued submissions on load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', retryQueuedSubmissions);
  } else {
    setTimeout(retryQueuedSubmissions, 1000);
  }

  // Public API
  return {
    captureEmail,
    saveLocalQuizData,
    getLocalQuizData,
    getDiscountCode,
    trackEvent,
    retryQueuedSubmissions
  };
})();
